import { useTranslation } from "react-i18next"
import { NavigationButtons } from "~/shared/components/NavigationButtons"

import { Alert, Text, Toast, useToast, LoadingSpinner } from "@oysterhr/ui"
import { useAccountAspect } from "@oysterhr/identity"
import { PaymentMethodsCustomerBankAccountAchType } from "@oysterhr/schema"
import { useNavigate } from "react-router-dom"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { useNewCompanyPaymentMethods } from "../../../../../hooks/useNewCompanyPaymentMethods"
import { useAuthorizePaymentMethod } from "../../../../../hooks/useAuthorizeDirectDebitOnPaymentMethod"
import { companyPaymentMethodsPath } from "../../../../../../../utils/routeHelpers"

export const AuthorizeDirectDebit = () => {
  const toast = useToast()
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/AchBankDetailsFlow")
  const { data, loading } = useNewCompanyPaymentMethods({
    variables: { companyId },
    fetchPolicy: "cache-and-network",
    onCompleted: data => {
      const customerBankAccount = data?.company.customerBankAccount
      // NOTE: At authorization step there must be a customer bank account, if it's not there or
      // if its different type than expected we redirect to payment methods page
      if (customerBankAccount?.__typename !== PaymentMethodEnumType.CUSTOMER_BANK_ACCOUNT_ACH) {
        navigate(companyPaymentMethodsPath(companyId))
      }
    },
  })

  const customerBankAccount = data?.company
    .customerBankAccount as PaymentMethodsCustomerBankAccountAchType

  const [authorize, { loading: authorizing }] = useAuthorizePaymentMethod()

  if (loading) {
    return <LoadingSpinner />
  }

  const onAuthorize = () => {
    authorize({
      variables: {
        authorized: true,
        paymentMethodId: customerBankAccount.id,
      },
      onCompleted: data => {
        if (data.authorizeDirectDebitOnPaymentMethod.errors.length) {
          toast({
            render: (
              <Toast variation="error">
                <Toast.Content>
                  {data?.authorizeDirectDebitOnPaymentMethod?.errors.join(", ")}
                </Toast.Content>
              </Toast>
            ),
          })
        } else {
          navigate(companyPaymentMethodsPath(companyId))
        }
      },
    })
  }

  return (
    <div className="mt-8">
      <Text>{t("authorization.title")}</Text>
      <Text size="large" className="mt-4">
        {t("authorization.subtitle")}
      </Text>

      <div className="mt-4">
        <Text className="font-semibold">{t("accountHolderName")}</Text>
        <Text>{customerBankAccount.accountHolderName}</Text>
      </div>
      <div className="mt-4">
        <Text className="font-semibold">{t("routingNumber")}</Text>
        <Text>{customerBankAccount.routingNumber}</Text>
      </div>
      <div className="mt-4">
        <Text className="font-semibold">{t("accountNumber")}</Text>
        <Text>{customerBankAccount.accountNumber}</Text>
      </div>

      <Alert variation="warning" className="mt-4">
        <Alert.Icon />
        <Alert.Content>{t("authorization.fteWarning")}</Alert.Content>
      </Alert>

      <NavigationButtons
        loading={authorizing}
        onNext={onAuthorize}
        isLast
        analyticsBase="ach-authorize-fte"
      />
    </div>
  )
}
